import { useMemo, useState } from 'react';
import { DECKS } from '../../config/decks';
import { CardContent, figureSrc } from '../../components/CardContent';
import * as storage from '../../services/storage/storage';
import type { AppData, Card, DeckId } from '../../types';

interface Props {
  cards: Record<DeckId, Card[]>;
  data: AppData;
  onSelectCard: (deckId: DeckId, card: Card) => void;
  onStartReview: (deckId: DeckId) => void;
  onDataChanged?: () => void;
}

type BrowseFilter = 'all' | 'new' | 'learning' | 'due' | 'review' | 'weak' | 'figure' | 'source' | 'suspended';

export function BrowseView({ cards, data, onSelectCard, onStartReview, onDataChanged }: Props) {
  const [deckId, setDeckId] = useState<DeckId>('bs');
  const [query, setQuery] = useState('');
  const [chapter, setChapter] = useState('all');
  const [filter, setFilter] = useState<BrowseFilter>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const deckCards = cards[deckId] ?? [];
  const chapters = Array.from(new Set(deckCards.map((card) => card.chapter)));
  const filtered = useMemo(() => deckCards.filter((card) => {
    const state = data.decks[deckId]?.cards?.[card.id];
    const isDue = state?.due ? new Date(state.due) <= new Date() : false;
    const haystack = `${card.front}\n${card.back}\n${card.chapter}`.toLowerCase();
    const matchesText = !query || haystack.includes(query.toLowerCase());
    const matchesChapter = chapter === 'all' || card.chapter === chapter;
    const matchesFilter = filter === 'all'
      || (filter === 'new' && (!state || state.state === 'new'))
      || (filter === 'learning' && state?.state === 'learning')
      || (filter === 'review' && state?.state === 'review')
      || (filter === 'due' && isDue)
      || (filter === 'weak' && ((state?.lapses ?? 0) > 0 || (state?.reviewCount ?? 0) > 0 && (state?.correctCount ?? 0) / Math.max(1, state?.reviewCount ?? 1) < 0.7))
      || (filter === 'figure' && Boolean(card.figure))
      || (filter === 'source' && Boolean(card.source))
      || (filter === 'suspended' && Boolean(state?.suspended));
    return matchesText && matchesChapter && matchesFilter;
  }), [chapter, data.decks, deckCards, deckId, filter, query]);

  function toggleSuspended(card: Card, suspended: boolean) {
    storage.setCardSuspended(deckId, card.id, suspended);
    onDataChanged?.();
  }

  return (
    <section className="view active">
      <header className="page-head">
        <div><h1>Karteikarten</h1><p className="muted">Durchsuche, filtere und verwalte alle Karten inklusive Abbildungen und Quellen.</p></div>
        <div className="head-actions">
          <button className="btn btn-primary" onClick={() => onStartReview(deckId)}>▶ Dieses Fach lernen</button>
        </div>
      </header>
      <div className="deck-tabs" role="tablist" aria-label="Karten-Fach wählen">
        {DECKS.map((deck) => <button key={deck.id} className={`deck-tab ${deckId === deck.id ? 'active' : ''}`} onClick={() => { setDeckId(deck.id); setChapter('all'); setExpanded(null); }}><span className="deck-tab-icon">{deck.icon}</span><span className="deck-tab-name">{deck.name}</span><span className="deck-tab-count">{cards[deck.id]?.length ?? 0}</span></button>)}
      </div>
      <div className="panel browse-tools">
        <div className="browse-search">
          <span className="browse-search-icon" aria-hidden>🔍</span>
          <input className="input" type="search" placeholder="Suche in Frage, Antwort oder Kapitel…" value={query} onChange={(event) => setQuery(event.target.value)} />
        </div>
        <label className="select-wrap">
          <span className="sr-only">Kapitel filtern</span>
          <select className="select" value={chapter} onChange={(event) => setChapter(event.target.value)}>
            <option value="all">Alle Kapitel ({chapters.length})</option>
            {chapters.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>
        <div className="filter-chips" role="group" aria-label="Kartenfilter">
          {(['all', 'new', 'learning', 'due', 'review', 'weak', 'figure', 'source', 'suspended'] as BrowseFilter[]).map((item) => <button key={item} className={`chip ${filter === item ? 'active' : ''}`} onClick={() => setFilter(item)}>{labelForFilter(item)}</button>)}
        </div>
        <span className="browse-count">{filtered.length} Treffer</span>
      </div>
      <div className="browse-list">
        {filtered.length === 0 && <p className="muted browse-empty">Keine Karten passen zum aktuellen Filter.</p>}
        {filtered.map((card) => {
          const state = data.decks[deckId]?.cards?.[card.id];
          const isExpanded = expanded === card.id;
          const isSuspended = Boolean(state?.suspended);
          const status = state?.state ?? 'new';
          return (
            <article key={card.id} className={`browse-row ${isExpanded ? 'expanded' : ''} ${isSuspended ? 'suspended' : ''}`}>
              <button className="browse-summary" onClick={() => setExpanded(isExpanded ? null : card.id)} aria-expanded={isExpanded}>
                <span className="browse-id">#{card.number}</span>
                <span className="browse-main">
                  <span className="browse-q">{card.front}</span>
                  <span className="browse-meta">
                    <span className={`status-pill status-${status}`}>{labelForStatus(status)}</span>
                    <span className="muted">{card.chapter}</span>
                    <span className="muted">{state?.reviewCount ?? 0}× Rev.</span>
                    {isSuspended && <span className="status-pill status-suspended">Versteckt</span>}
                    {card.figure && <span className="badge-mini" title="Mit Abbildung">🖼</span>}
                    {card.source && <span className="badge-mini" title="Mit Quelle">🔗</span>}
                  </span>
                </span>
                <span className="browse-chevron" aria-hidden>{isExpanded ? '▾' : '▸'}</span>
              </button>
              {isExpanded && <div className="browse-details">
                <div className="browse-columns"><div><h4>Vorderseite</h4><CardContent text={card.front} /></div><div><h4>Rückseite</h4><CardContent text={card.back} /></div></div>
                {card.figure && <img className="browse-figure" src={figureSrc(card.figure) ?? undefined} loading="lazy" alt={`Abbildung zu Karte ${card.number}`} />}
                <div className="browse-actions">
                  <button className="btn btn-secondary" onClick={() => onSelectCard(deckId, card)}>💬 Im Tutor verwenden</button>
                  <button className="btn btn-secondary" onClick={() => onStartReview(deckId)}>🎯 Fach lernen</button>
                  {isSuspended ? (
                    <button className="btn btn-secondary" onClick={() => toggleSuspended(card, false)}>👁 Wieder einblenden</button>
                  ) : (
                    <button className="btn btn-secondary danger-outline" onClick={() => toggleSuspended(card, true)}>🚫 Karte ausblenden</button>
                  )}
                  {card.source && <a className="btn btn-secondary" href={card.source} target="_blank" rel="noreferrer">↗ Quelle</a>}
                </div>
              </div>}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function labelForFilter(filter: BrowseFilter) {
  return ({ all: 'Alle', new: 'Neu', learning: 'Lernen', due: 'Fällig', review: 'Review', weak: 'Schwach', figure: 'Abbildung', source: 'Quelle', suspended: 'Versteckt' } satisfies Record<BrowseFilter, string>)[filter];
}

function labelForStatus(state: string) {
  return ({ new: 'Neu', learning: 'Lernen', review: 'Review' } as Record<string, string>)[state] ?? state;
}
